import { NativeEventEmitter, NativeModules, Platform } from 'react-native';
import { createWatchCorrelationId, watchError, watchInfo, watchWarn } from '../telemetry';

const NATIVE_MODULE_NAME = 'WatchBridgeNativeModule';
const EVENT_SYNC_STATUS = 'watchSyncStatus';

// Module-level singleton — NativeEventEmitter must not be re-created per
// subscription call or the native listener count drifts and leaks.
let _emitter: NativeEventEmitter | null = null;
function getEmitter(nativeModule: NativeWatchBridge): NativeEventEmitter {
  if (!_emitter) {
    _emitter = new NativeEventEmitter(nativeModule as never);
  }
  return _emitter;
}

type NativeWatchBridge = {
  activateSession?: () => Promise<boolean>;
  getStatus?: () => Promise<WatchConnectivityStatus>;
  getBackgroundRefreshStatus?: () => Promise<string>;
  updateApplicationContext?: (payload: Record<string, unknown>) => Promise<boolean>;
  sendMessage?: (payload: Record<string, unknown>) => Promise<boolean>;
  transferNoteAudio?: (noteId: string, urlString: string, authToken: string) => Promise<boolean>;
  addListener?: (eventName: string) => void;
  removeListeners?: (count: number) => void;
};

export type WatchConnectivityStatus = {
  isSupported: boolean;
  isPaired: boolean;
  isWatchAppInstalled: boolean;
  isReachable: boolean;
  activationState: number;
};

export type WatchSyncStatusEvent = {
  correlationId: string;
  localId?: string;
  projectId?: string;
  status: 'queued' | 'syncing' | 'synced' | 'failed';
  errorCode?: string | null;
  // Inbound events forwarded from the watch (request_plan_data, request_emergency_data,
  // file_received, activation, application_context, etc.)
  type?: string;
  payload?: Record<string, unknown>;
};

const DEFAULT_STATUS: WatchConnectivityStatus = {
  isSupported: false,
  isPaired: false,
  isWatchAppInstalled: false,
  isReachable: false,
  activationState: 0,
};

function getNativeModule(): NativeWatchBridge | null {
  if (Platform.OS !== 'ios') return null;
  return (NativeModules as Record<string, NativeWatchBridge | undefined>)[NATIVE_MODULE_NAME] || null;
}

export function isWatchBridgeAvailable(): boolean {
  return !!getNativeModule();
}

export async function activateWatchSession(): Promise<boolean> {
  const nativeModule = getNativeModule();
  if (!nativeModule?.activateSession) {
    watchInfo('watch-connectivity', 'Watch native module unavailable during activate', { platform: Platform.OS });
    return false;
  }
  try {
    return await nativeModule.activateSession();
  } catch (error) {
    watchError('Watch activateSession failed', { error });
    return false;
  }
}

/**
 * Returns the iOS Background App Refresh status for this app:
 * "available" | "denied" | "restricted" | "unknown"
 * Always returns "available" on Android or when the native module is absent.
 */
export async function getBackgroundRefreshStatus(): Promise<'available' | 'denied' | 'restricted' | 'unknown'> {
  const nativeModule = getNativeModule();
  if (!nativeModule?.getBackgroundRefreshStatus) return 'available';
  try {
    const status = await nativeModule.getBackgroundRefreshStatus();
    return (status as 'available' | 'denied' | 'restricted' | 'unknown') ?? 'unknown';
  } catch {
    return 'unknown';
  }
}

export async function getWatchConnectivityStatus(): Promise<WatchConnectivityStatus> {
  const nativeModule = getNativeModule();
  if (!nativeModule?.getStatus) return DEFAULT_STATUS;
  try {
    return await nativeModule.getStatus();
  } catch (error) {
    watchWarn('Watch getStatus failed, using defaults', { error });
    return DEFAULT_STATUS;
  }
}

/**
 * True when an error from the native bridge is the WatchConnectivity
 * "no paired Apple Watch" / "watch app not installed" family. These
 * surfaces are normal on a developer iPhone simulator without a paired
 * Watch sim, so we downgrade them to info-level rather than error.
 *
 * The native Swift layer in WatchBridgeNativeModule already short-circuits
 * unpaired pushes (see the `guard session.isPaired` checks); this filter
 * is the defense-in-depth path for any rejection that still bubbles up —
 * e.g. a real device that lost pairing mid-session, or a future bridge
 * change that drops the Swift guard.
 */
function isUnpairedWatchError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  if (!message) return false;
  // Apple's localized strings. WCErrorCodeDeviceNotPaired = "Device is not
  // paired."; WCErrorCodeWatchAppNotInstalled = "WatchApp is not installed
  // on the paired iPhone." (and a few historical variants).
  return (
    message.includes('Device is not paired') ||
    message.includes('not paired') ||
    message.includes('WCErrorCodeDeviceNotPaired') ||
    message.includes('Watch app is not installed') ||
    message.includes('WatchApp is not installed') ||
    message.includes('WCErrorCodeWatchAppNotInstalled')
  );
}

export async function pushWatchContext(payload: Record<string, unknown>): Promise<{
  ok: boolean;
  correlationId: string;
}> {
  const correlationId = createWatchCorrelationId('ctx');
  const nativeModule = getNativeModule();
  if (!nativeModule?.updateApplicationContext) {
    watchInfo('watch-connectivity', 'Skipped updateApplicationContext (native module unavailable)', { correlationId });
    return { ok: false, correlationId };
  }
  try {
    const ok = await nativeModule.updateApplicationContext({
      ...payload,
      correlationId,
    });
    return { ok, correlationId };
  } catch (error) {
    if (isUnpairedWatchError(error)) {
      watchInfo('watch-connectivity', 'Skipped updateApplicationContext (no paired watch / app not installed)', {
        correlationId,
      });
      return { ok: false, correlationId };
    }
    watchError('Watch updateApplicationContext failed', { correlationId, error });
    return { ok: false, correlationId };
  }
}

export async function sendWatchMessage(payload: Record<string, unknown>): Promise<{
  ok: boolean;
  correlationId: string;
}> {
  const correlationId = createWatchCorrelationId('msg');
  const nativeModule = getNativeModule();
  if (!nativeModule?.sendMessage) {
    watchInfo('watch-connectivity', 'Skipped sendMessage (native module unavailable)', { correlationId });
    return { ok: false, correlationId };
  }
  try {
    const ok = await nativeModule.sendMessage({
      ...payload,
      correlationId,
    });
    return { ok, correlationId };
  } catch (error) {
    if (isUnpairedWatchError(error)) {
      watchInfo('watch-connectivity', 'Skipped sendMessage (no paired watch / app not installed)', { correlationId });
      return { ok: false, correlationId };
    }
    watchError('Watch sendMessage failed', { correlationId, error });
    return { ok: false, correlationId };
  }
}

export async function transferNoteAudio(noteId: string, urlString: string, authToken: string): Promise<boolean> {
  const nativeModule = getNativeModule();
  if (!nativeModule?.transferNoteAudio) return false;
  try {
    return await nativeModule.transferNoteAudio(noteId, urlString, authToken);
  } catch (error) {
    watchError('Watch transferNoteAudio failed', { noteId, error });
    return false;
  }
}

export function onWatchSyncStatus(listener: (event: WatchSyncStatusEvent) => void): () => void {
  const nativeModule = getNativeModule();
  if (!nativeModule) {
    return () => {};
  }
  const emitter = getEmitter(nativeModule);
  const subscription = emitter.addListener(EVENT_SYNC_STATUS, listener);
  return () => {
    try {
      subscription.remove();
    } catch {}
  };
}
